import { Checkbox } from '@europrocurement/flexy-components';
import { faBell, faCalendar, faEdit, faTrash, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import React, { useEffect, useState } from 'react';

import { type DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { type PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { type UtilisateurTaches } from '@europrocurement/l2d-domain';
import TaskPriorityChip from '../TaskPriorityChip/TaskPriorityChip';

type CheckableTaskRowProps = {
    task: DossierTaches | PrescripteurTaches | UtilisateurTaches;
    handleCheckTask: (linkedTask: DossierTaches | PrescripteurTaches | UtilisateurTaches) => void;
    onEditTask: (linkedTask: DossierTaches | PrescripteurTaches | UtilisateurTaches) => void;
    onDeleteTask: (id: number) => void;
    canDeleteTask: boolean;
};

const CheckableTaskRow: React.FunctionComponent<CheckableTaskRowProps> = function ({
    task,
    handleCheckTask,
    onEditTask,
    onDeleteTask,
    canDeleteTask = false,
}) {
    const { palette } = useTheme();

    const [taskExpiredColor, setTaskExpiredColor] = useState(palette.text.primary);

    useEffect(() => {
        if (task.tache) {
            // Dates alert colors
            if (task.tache?.expiredAt) {
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const taskExpired = new Date(task.tache.expiredAt);
                taskExpired.setHours(0, 0, 0, 0);
                if (taskExpired < now) {
                    setTaskExpiredColor(palette.error.main);
                } else if (taskExpired.getTime() === now.getTime()) {
                    setTaskExpiredColor(palette.warning.main);
                } else {
                    setTaskExpiredColor(palette.text.primary);
                }
            }
        }
    }, [task, palette]);

    if (!task.tache) {
        return <Box />;
    }
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${palette.grey['200']}`,
                borderRadius: '4px',
                padding: '4px 8px',
                width: '100%',
                gap: '8px',
                boxShadow: '0px 2px 2.5px 0px rgba(193, 202, 215, 0.25)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: '8px',
                }}
            >
                <Typography sx={{ textDecoration: task.tache.isDone ? 'line-through' : 'none' }}>
                    {task.tache.title}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '4px',
                        }}
                    >
                        <FontAwesomeIcon icon={faUser} />
                        <Typography variant="l2dcaption">
                            {task.tache.assignTo?.nom} {task.tache.assignTo?.prenom}
                        </Typography>
                    </Box>

                    <TaskPriorityChip taskPriority={task.tache.priority} />

                    <Checkbox
                        key={`'checkBox'-${task.id}`}
                        checked={task.tache.isDone}
                        onChange={() => handleCheckTask(task)}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography>{task.tache.description}</Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                        {task.tache.expiredAt && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: taskExpiredColor,
                                }}
                            >
                                <FontAwesomeIcon icon={faCalendar} />
                                {task.tache.expiredAt && (
                                    <Typography variant="l2dcaption">
                                        {new Date(task.tache.expiredAt).toLocaleDateString()}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {task.tache.remindAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FontAwesomeIcon icon={faBell} />
                                {task.tache.remindAt && (
                                    <Typography variant="l2dcaption">
                                        {new Date(task.tache.remindAt).toLocaleDateString()}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '4px',
                            fontStyle: 'italic',
                            color: palette.grey['700'],
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="l2dcaption">
                            Créé par {task.tache.createdBy?.nom} {task.tache.createdBy?.prenom}
                        </Typography>
                        {task.tache.createdAt && (
                            <Typography variant="l2dcaption">
                                le {new Date(task.tache.createdAt).toLocaleDateString()}
                            </Typography>
                        )}
                        <Box>
                            <Button
                                onClick={() => onEditTask(task)}
                                sx={{ minWidth: '8px' }}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            {canDeleteTask && task.id && (
                                <Button
                                    onClick={() => onDeleteTask(task.id as number)}
                                    sx={{ minWidth: '8px', color: palette.error.main }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CheckableTaskRow;
